import { useState, useEffect } from 'react';
import { X, Mail, Calendar, User, ChevronRight } from 'lucide-react';
import { emailsAPI } from '@/utils/api';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';
import SafeEmailBody from './SafeEmailBody';

/**
 * EmailListOverlay - Modal for viewing a list of emails with reading pane
 * Used for:
 * - Viewing AI search results
 * - Previewing emails before action approval
 */
export default function EmailListOverlay({ emailIds, onClose, title = 'Emails' }) {
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch email details
  useEffect(() => {
    async function fetchEmails() {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch details for all email IDs
        const promises = emailIds.map(id => emailsAPI.getById(id));
        const results = await Promise.all(promises);
        setEmails(results.map(r => r.data));

        // Select first email by default
        if (results.length > 0) {
          setSelectedEmail(results[0].data);
        }
      } catch (err) {
        console.error('Failed to fetch emails:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    }

    if (emailIds && emailIds.length > 0) {
      fetchEmails();
    }
  }, [emailIds]);

  // Fetch full body when email is selected
  useEffect(() => {
    async function fetchBody() {
      if (!selectedEmail || selectedEmail.body) return;

      try {
        const response = await emailsAPI.getBody(selectedEmail.id);
        setSelectedEmail(response.data);
        // Update in emails list too
        setEmails(prev => prev.map(e =>
          e.id === selectedEmail.id ? response.data : e
        ));
      } catch (err) {
        console.error('Failed to fetch email body:', err);
      }
    }

    fetchBody();
  }, [selectedEmail?.id]);

  function formatDate(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  }

  function getCategoryColor(category) {
    const colors = {
      work: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      personal: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      finance: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
      social: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
      updates: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300',
      marketing: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
      spam: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    };
    return colors[category] || colors.updates;
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-xl border max-w-7xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">
              {title} {emails.length > 0 && `(${emails.length})`}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {isLoading ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3">
              <LoadingSpinner size="lg" />
              <p className="text-sm text-muted-foreground">Loading emails...</p>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <p className="text-red-600 dark:text-red-400 mb-2">Failed to load emails</p>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
            </div>
          ) : emails.length === 0 ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <p className="text-muted-foreground">No emails to display</p>
            </div>
          ) : (
            <>
              {/* Email List */}
              <div className="w-80 border-r overflow-y-auto">
                <div className="divide-y">
                  {emails.map((email) => (
                    <button
                      key={email.id}
                      onClick={() => setSelectedEmail(email)}
                      className={`w-full p-3 text-left hover:bg-accent transition-colors ${
                        selectedEmail?.id === email.id ? 'bg-accent' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <span className={`text-sm font-medium truncate ${email.seen ? 'font-normal text-muted-foreground' : 'font-semibold'}`}>
                          {email.from.split('<')[0].trim() || email.from}
                        </span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatDate(email.date)}
                        </span>
                      </div>
                      <p className={`text-sm truncate mb-1 ${email.seen ? 'text-muted-foreground' : ''}`}>
                        {email.subject}
                      </p>
                      {email.summary && (
                        <p className="text-xs text-muted-foreground truncate">
                          {email.summary}
                        </p>
                      )}
                      <div className="flex items-center gap-1 mt-1">
                        {email.category && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${getCategoryColor(email.category)}`}>
                            {email.category}
                          </span>
                        )}
                        {email.priority && email.priority !== 'normal' && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                            {email.priority}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Reading Pane */}
              <div className="flex-1 overflow-y-auto">
                {selectedEmail ? (
                  <div className="p-6">
                    {/* Email Header */}
                    <div className="mb-6">
                      <h3 className="text-xl font-semibold mb-3">
                        {selectedEmail.subject}
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <User className="h-4 w-4" />
                          <span className="font-medium">From:</span>
                          <span>{selectedEmail.from}</span>
                        </div>
                        {selectedEmail.to && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <ChevronRight className="h-4 w-4" />
                            <span className="font-medium">To:</span>
                            <span>{selectedEmail.to}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          <span className="font-medium">Date:</span>
                          <span>{new Date(selectedEmail.date).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-3">
                        {selectedEmail.category && (
                          <span className={`text-xs px-2 py-1 rounded ${getCategoryColor(selectedEmail.category)}`}>
                            {selectedEmail.category}
                          </span>
                        )}
                        {selectedEmail.priority && selectedEmail.priority !== 'normal' && (
                          <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                            Priority: {selectedEmail.priority}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Email Body */}
                    <div className="border-t pt-6">
                      {selectedEmail.body?.text ? (
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <pre className="whitespace-pre-wrap font-sans text-sm">
                            {selectedEmail.body.text}
                          </pre>
                        </div>
                      ) : selectedEmail.body?.html ? (
                        <SafeEmailBody html={selectedEmail.body.html} className="prose prose-sm dark:prose-invert max-w-none" />
                      ) : (
                        <p className="text-muted-foreground italic">Loading email content...</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    Select an email to view
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
